import type { LaunchState } from '@/lib/launchpad/types'
import type { SimPresale } from '@/lib/presale/types'
import { tokenSharePath } from '@/lib/shareSlug'

export type TokenSearchKind = 'curve' | 'presale'

export interface TokenSearchResult {
  id: string
  name: string
  ticker: string
  imageUrl?: string
  contractAddress?: string
  shareSlug?: string
  tokenAddress?: string
  path: string
  kind: TokenSearchKind
}

const MAX_RESULTS = 8

function rankFor(item: { name: string, ticker: string }, query: string): number {
  const name = item.name.toLowerCase()
  const ticker = item.ticker.toLowerCase()
  if (ticker === query || name === query)
    return 0
  if (ticker.startsWith(query) || name.startsWith(query))
    return 1
  return 2
}

export function searchTokens(
  query: string,
  launches: LaunchState[],
  presales: SimPresale[],
): TokenSearchResult[] {
  const q = query.trim().toLowerCase()
  if (!q)
    return []

  const launchResults: TokenSearchResult[] = launches
    .filter(launch => launch.name.toLowerCase().includes(q) || launch.ticker.toLowerCase().includes(q))
    .map(launch => ({
      id: launch.id,
      name: launch.name,
      ticker: launch.ticker,
      imageUrl: launch.imageUrl || undefined,
      contractAddress: launch.contractAddress,
      shareSlug: launch.shareSlug,
      tokenAddress: launch.contractAddress,
      path: tokenSharePath(launch.shareSlug, launch.id),
      kind: 'curve' as const,
    }))

  const presaleResults: TokenSearchResult[] = presales
    .filter(presale => presale.name.toLowerCase().includes(q) || presale.ticker.toLowerCase().includes(q))
    .map(presale => ({
      id: presale.id,
      name: presale.name,
      ticker: presale.ticker,
      imageUrl: presale.imageUrl,
      contractAddress: presale.contractAddress,
      shareSlug: presale.shareSlug,
      tokenAddress: presale.tokenAddress,
      path: tokenSharePath(presale.shareSlug, presale.id),
      kind: 'presale' as const,
    }))

  return [...launchResults, ...presaleResults]
    .sort((a, b) => rankFor(a, q) - rankFor(b, q))
    .slice(0, MAX_RESULTS)
}
